import { ApolloProvider } from '@apollo/client';
import { SessionProvider } from 'next-auth/react';
import { ToastProvider } from 'providers/ToastProvider';
import VGSProvider from 'components/VGSProvider';
import IntercomProvider from 'providers/IntercomProvider';
import CanduProvider from 'providers/CanduProvider';
import UnitComponentsProvider from 'providers/UnitComponentsProvider';
import { HBUIProvider, TestIdBoundaryProvider } from '@honeybook/hbui';
import { LocalizationProvider } from '@honeybook/i18n-react';
import ModalProvider from 'providers/ModalProvider';
import FullStory from 'lib/client/FullStory';
import useApollo from 'apollo-client';
import ErrorBoundary from 'components/ErrorBoundary';
import { Session } from 'next-auth';
import { GlobalStyles } from 'styles/globals';
import SegmentProvider from 'providers/SegmentProvider';
import RouteListener from 'lib/client/RouteListener';
import DatadogRumInit from 'lib/client/datadogRum';
import TwoFactorAuthProvider from 'components/two-factor/TwoFactorAuthProvider';
import { GatesProvider } from './GatesProvider';
import { SessionMonitor } from 'components/SessionMonitor/SessionMonitor';
import { AuthLinkProvider } from 'lib/comlink/AuthLink';
import { HiddenFeaturesProvider } from 'providers/HiddenFeaturesProvider';
import { SideNavHandler } from 'components/SideNavSupport/SideNavHandler';
import { ColorModeHandler } from 'components/ColorModeSupport/ColorModeHandler';
import { AnalyticsProviderWrapper, FinanceExternalWrapper } from 'components/FinanceExternalWrapper';
import { UIPreferencesContextProvider } from '@honeybook/finance-components/dist/hooks/useUIPreferences';
import { ServerUIPreference } from '@honeybook/finance-components/dist/lib/UIPreferences.types';
import { CurrencyProvider } from '@honeybook/finance-components';
import ClientOnly from '../components/ClientOnly';
import { logger } from '@finance/shared';
import { useRouter } from 'next/router';

interface GlobalProviderProps {
    children: React.ReactNode;
    session: Session;
    appPath?: string;
    uiPreferences?: ServerUIPreference[];
    clientOnly?: boolean;
}
const GlobalProviders = ({ children, session, appPath, uiPreferences, clientOnly }: GlobalProviderProps) => {
    const apolloClient = useApollo();

    return (
        <ErrorBoundary>
            <VGSProvider />
            <UnitComponentsProvider />
            <GlobalStyles />
            <RouteListener />
            <AuthLinkProvider>
                <HiddenFeaturesProvider>
                    <ToastProvider>
                        <SessionProvider session={session} refetchInterval={30 * 60} refetchOnWindowFocus={true}>
                            <DatadogRumInit />
                            <SessionMonitor />
                            <ClientOnlyWrapper lazy={Boolean(clientOnly)}>
                                <GatesProvider>
                                    <SideNavHandler />
                                    <ColorModeHandler />
                                    <IntercomProvider />
                                    <CanduProvider />
                                    <FullStory />
                                    <ApolloProvider client={apolloClient}>
                                        <TestIdBoundaryProvider prefix={'finance'}>
                                            <LocalizationProvider locale={session?.user?.company?.locale}>
                                                <HBUIProvider value={{}}>
                                                    <CurrencyProvider currency={session?.user?.company?.currency ?? 'USD'}>
                                                        <UIPreferencesContextProvider uiPreferences={uiPreferences}>
                                                            <SegmentProvider>
                                                                <TwoFactorAuthProvider>
                                                                    <AnalyticsProviderWrapper appPath={appPath}>
                                                                        <ModalProvider>
                                                                            <FinanceExternalWrapper appPath={appPath} uiPreferences={false}>
                                                                                {children}
                                                                            </FinanceExternalWrapper>
                                                                        </ModalProvider>
                                                                    </AnalyticsProviderWrapper>
                                                                </TwoFactorAuthProvider>
                                                            </SegmentProvider>
                                                        </UIPreferencesContextProvider>
                                                    </CurrencyProvider>
                                                </HBUIProvider>
                                            </LocalizationProvider>
                                        </TestIdBoundaryProvider>
                                    </ApolloProvider>
                                </GatesProvider>
                            </ClientOnlyWrapper>
                        </SessionProvider>
                    </ToastProvider>
                </HiddenFeaturesProvider>
            </AuthLinkProvider>
        </ErrorBoundary>
    );
};

const ClientOnlyWrapper: React.FC<{ lazy: boolean }> = props => {
    const { pathname } = useRouter();
    if (!props.lazy) {
        logger.debug({ pathname }, 'ClientOnlyWrapper: rendering with ssr');
        return <>{props.children}</>;
    }

    logger.debug({ pathname }, 'ClientOnlyWrapper: rendering with client only');
    return <ClientOnly>{props.children}</ClientOnly>;
};

export default GlobalProviders;
